import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useStatsigClient } from "@statsig/react-bindings";

// Collapse dynamic path segments so events group by route shape, not specific IDs
const normalizePath = (pathname: string): string =>
  pathname
    .replace(/\/newsletterEdition\/[^/]+/, "/newsletterEdition/:id")
    .replace(/\/blueprints\/[^/]+/, "/blueprints/:id")
    .replace(/\/subscribers\/[^/]+/, "/subscribers/:id")
    .replace(/\/journeys\/[^/]+/, "/journeys/:id")
    .replace(/\/segments\/[^/]+/, "/segments/:id")
    .replace(/\/insights\/[^/]+/, "/insights/:id")
    .replace(/\/editions\/[^/]+/, "/editions/:id")
    .replace(/\/templates\/[^/]+/, "/templates/:id")
    .replace(/\/requests\/view\/[^/]+/, "/requests/view/:id")
    .replace(/\/self-interviews\/[^/]+/, "/self-interviews/:id")
    .replace(/\/team\/[^/]+/, "/team/:id");

/**
 * Tracks time spent on each page and fires a `page_time_spent` event to Statsig.
 * Event value: duration in seconds.
 * Event metadata: { page: normalized_path }
 *
 * Must be rendered inside both <StatsigProvider> and <Router>.
 */
const PageTimeTracker: React.FC = () => {
  const location = useLocation();
  const { client } = useStatsigClient();
  const entryTimeRef = useRef<number>(Date.now());
  const currentPageRef = useRef<string>(normalizePath(location.pathname));

  // Fire when the route changes
  useEffect(() => {
    const newPage = normalizePath(location.pathname);
    if (newPage === currentPageRef.current) return;

    const durationSeconds = Math.round((Date.now() - entryTimeRef.current) / 1000);
    client.logEvent("page_time_spent", durationSeconds, { page: currentPageRef.current });

    entryTimeRef.current = Date.now();
    currentPageRef.current = newPage;
  }, [location.pathname, client]);

  // Fire when the tab closes / user navigates away
  useEffect(() => {
    const handleUnload = () => {
      const durationSeconds = Math.round((Date.now() - entryTimeRef.current) / 1000);
      client.logEvent("page_time_spent", durationSeconds, { page: currentPageRef.current });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [client]);

  return null;
};

export default PageTimeTracker;
